import React, { useEffect, useState } from "react";
import Button, { Icons } from "../../components/buttons/Button";
import DeleteConfirmation from "./DeleteConfirmation";
import NumberField from "../../components/inputs/NumberField";
import HexInput from "../HexInput";
import { EDIT_XSSI_CONNECTION_TRANSLATIONS } from "../../translations/util";
import {
  XSSI_GROUP_RANGE_MAX_VALUE,
  XSSI_RANGE_MIN_VALUE,
  XSSI_UNIT_RANGE_MAX_VALUE,
} from "../../constants";

interface IProps {
  range: {
    min: number;
    max: number;
    id: number;
    modificationDate: string;
    creationDate: string;
  };
  deleteRange: () => void;
  updateRange: (min: number, max: number, id: number) => void;
  labelPrefix?: string;
  editloading?: boolean;
  deleteLoading?: boolean;
  hasEditPermission: boolean;
  hasDeletePermission: boolean;
}

const UpdateExistingRange = ({
  range,
  deleteRange,
  updateRange,
  labelPrefix = "",
  editloading,
  deleteLoading,
  hasEditPermission,
  hasDeletePermission,
}: IProps) => {
  const [editing, setEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [min, setMin] = useState<number | undefined>(range.min);
  const [max, setMax] = useState<number | undefined>(range.max);

  const isMaxInvalid = (
    newMax?: number,
    newMin?: number,
    upperLimit?: number
  ) => {
    if (newMax === undefined) {
      return false;
    }

    if (upperLimit && newMax > upperLimit) return true;
    if (newMax <= (newMin || 1)) return true;
    if (newMax < 1) return true;

    return false;
  };

  const isMinInvalid = (
    newMax?: number,
    newMin?: number,
    upperLimit?: number
  ) => {
    if (newMin === undefined) {
      return false;
    }

    if (newMin < 1) return true;
    if (upperLimit && newMin >= (newMax || upperLimit)) return true;
    if (upperLimit && newMin > upperLimit) return true;

    return false;
  };

  const isAddtoRangeButtonInValid = (
    newMax?: number,
    newMin?: number,
    upperLimit?: number
  ) => {
    if (newMax === undefined || newMin === undefined) {
      return true;
    }
    return (
      isMaxInvalid(newMax, newMin, upperLimit) ||
      isMinInvalid(newMax, newMin, upperLimit)
    );
  };

  useEffect(() => {
    setMax(range.max);
    setMin(range.min);
  }, [range]);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {isDeleting ? (
        <DeleteConfirmation
          loading={deleteLoading}
          onCancel={() => setIsDeleting(false)}
          onDelete={deleteRange}
        />
      ) : (
        <div className="data-card">
          <div className="flex justify-between">
            <ul className="extra-info">
              <li data-cy={`${labelPrefix}-creationDate`}>
                <span>
                  {EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels.creationDate}:
                </span>{" "}
                {range.creationDate}
              </li>
              <li data-cy={`${labelPrefix}-modificationDate`}>
                <span>
                  {
                    EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels
                      .modificationDate
                  }
                  :
                </span>{" "}
                {range.modificationDate}
              </li>
            </ul>

            {editing ? (
              <div className="extra-action">
                <Button
                  kind="tertiary"
                  className="mr-3"
                  type="button"
                  disabled={false}
                  label={
                    EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels.cancelButton
                  }
                  onClick={() => {
                    setMax(range.max);
                    setMin(range.min);
                    setEditing(false);
                  }}
                  dataCy={`${labelPrefix}-cancelEditXSSIRange-`}
                />

                <Button
                  className=""
                  type="button"
                  disabled={isAddtoRangeButtonInValid(
                    max,
                    min,
                    labelPrefix === "Group"
                      ? XSSI_GROUP_RANGE_MAX_VALUE
                      : XSSI_UNIT_RANGE_MAX_VALUE
                  )}
                  label={
                    EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels.saveButton
                  }
                  loading={editloading}
                  onClick={() => {
                    if (min !== undefined && max !== undefined) {
                      updateRange(min, max, range.id);
                      setEditing(false);
                    }
                  }}
                  dataCy={`${labelPrefix}-saveEditedXSSIRange-`}
                />
              </div>
            ) : (
              <div className="extra-action">
                {hasEditPermission ? (
                  <Button
                    kind="tertiary"
                    className="border-none no-outline p-0 mr-3"
                    type="button"
                    disabled={!hasEditPermission}
                    icon={Icons.Edit}
                    label={
                      EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels.editButton
                    }
                    onClick={() => setEditing(true)}
                    dataCy={`edit${labelPrefix}Range-`}
                  />
                ) : null}

                {hasDeletePermission ? (
                  <Button
                    kind="tertiary"
                    className="border-none no-outline p-0"
                    type="button"
                    disabled={!hasDeletePermission}
                    icon={Icons.Delete}
                    label={
                      EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels
                        .deleteButton
                    }
                    onClick={() => setIsDeleting(true)}
                    dataCy={`delete${labelPrefix}Range-`}
                  />
                ) : null}
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4 form-control mt-6">
            <NumberField
              label={EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels.id}
              max={
                labelPrefix === "Group"
                  ? XSSI_GROUP_RANGE_MAX_VALUE
                  : XSSI_UNIT_RANGE_MAX_VALUE
              }
              min={XSSI_RANGE_MIN_VALUE}
              disabled
              onChange={() => null}
              value={range.id}
            />
            <HexInput
              label={EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels.minId(
                labelPrefix
              )}
              max={
                labelPrefix === "Group"
                  ? XSSI_GROUP_RANGE_MAX_VALUE
                  : XSSI_UNIT_RANGE_MAX_VALUE
              }
              min={XSSI_RANGE_MIN_VALUE}
              disabled={!editing}
              value={min}
              onChange={(val) => setMin(val || val === 0 ? +val : undefined)}
              invalid={isMinInvalid(
                max,
                min,
                labelPrefix === "Group"
                  ? XSSI_GROUP_RANGE_MAX_VALUE
                  : XSSI_UNIT_RANGE_MAX_VALUE
              )}
              invalidText={EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels.minInvalidText(
                labelPrefix,
                XSSI_RANGE_MIN_VALUE,
                labelPrefix === "Group"
                  ? XSSI_GROUP_RANGE_MAX_VALUE
                  : XSSI_UNIT_RANGE_MAX_VALUE
              )}
              tooltipText={EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels.minToolTipText(
                labelPrefix,
                XSSI_RANGE_MIN_VALUE,
                labelPrefix === "Group"
                  ? XSSI_GROUP_RANGE_MAX_VALUE
                  : XSSI_UNIT_RANGE_MAX_VALUE
              )}
              required
            />
            <HexInput
              label={EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels.maxId(
                labelPrefix
              )}
              max={
                labelPrefix === "Group"
                  ? XSSI_GROUP_RANGE_MAX_VALUE
                  : XSSI_UNIT_RANGE_MAX_VALUE
              }
              min={XSSI_RANGE_MIN_VALUE}
              disabled={!editing}
              value={max}
              onChange={(val) => setMax(val || val === 0 ? +val : undefined)}
              invalid={isMaxInvalid(
                max,
                min,
                labelPrefix === "Group"
                  ? XSSI_GROUP_RANGE_MAX_VALUE
                  : XSSI_UNIT_RANGE_MAX_VALUE
              )}
              invalidText={EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels.maxInvalidText(
                labelPrefix,
                XSSI_RANGE_MIN_VALUE,
                labelPrefix === "Group"
                  ? XSSI_GROUP_RANGE_MAX_VALUE
                  : XSSI_UNIT_RANGE_MAX_VALUE
              )}
              tooltipText={EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels.maxToolTipText(
                labelPrefix,
                XSSI_RANGE_MIN_VALUE,
                labelPrefix === "Group"
                  ? XSSI_GROUP_RANGE_MAX_VALUE
                  : XSSI_UNIT_RANGE_MAX_VALUE
              )}
              required
            />
          </div>
        </div>
      )}
    </>
  );
};

export default UpdateExistingRange;
